// 'use client';

import React, { useState, useMemo } from 'react';
import { AreaChart, BarChart, Card } from '@tremor/react';
import { currencyFormat } from '../../../utils/number';
import { isMobile } from 'react-device-detect';
import { months } from '../../../utils/helper';
import { Tooltip } from '@mui/material';

const valueFormatter = (number) => {
    const options = { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 3 };
    return 'R$ ' + Intl.NumberFormat('pt-BR').format(number, options).toString();
};

export default function AreaChartPerformance({ performanceByStore, calculateTimesByStore }) {
    const [groupBy, setGroupBy] = useState('month'); // 'day' | 'month'
    const [sortBy, setSortBy] = useState('ALPHABETICAL'); // 'ALPHABETICAL' | 'GROWTH' | 'REVENUE'

    // Pré-calcula métricas por unidade para permitir ordenação consistente
    const unitsWithMetrics = useMemo(() => {
        const data = Array.isArray(performanceByStore?.data) ? performanceByStore.data : [];
        const today = new Date();
        const realMonth = today.getMonth() + 1;

        const findMonth = (arr, m) => (arr || []).find((x) => x.month === m) || { days: [] };
        const sumUpTo = (days = [], cutoffDay) =>
            days.reduce((sum, d) => {
                const v = parseFloat(d?.valorTotal ?? 0);
                if (cutoffDay == null) return sum + v; // mês inteiro
                return sum + ((d?.day ?? 0) <= cutoffDay ? v : 0);
            }, 0);

        return data.map((unit) => {
            const isSelectedCurrentMonth = unit?.isCurrentMonth ?? (unit?.currentMonth === realMonth);

            const currentMonthObj = findMonth(unit.months, unit.currentMonth);
            const prevMonthNum = unit.currentMonth === 1 ? 12 : unit.currentMonth - 1;
            const prevMonthObj = findMonth(unit.months, prevMonthNum);

            let totalCurrent, totalPrev;
            if (isSelectedCurrentMonth) {
                const cutoffDay =
                    unit?.currentDay ?? Math.max(0, ...currentMonthObj.days.map((d) => d?.day ?? 0));
                totalCurrent = sumUpTo(currentMonthObj.days, cutoffDay);
                totalPrev = sumUpTo(prevMonthObj.days, cutoffDay);
            } else {
                totalCurrent = sumUpTo(currentMonthObj.days, null);
                totalPrev = sumUpTo(prevMonthObj.days, null);
            }

            const growthPct = totalPrev > 0 ? ((totalCurrent - totalPrev) / totalPrev) * 100 : 0;

            return {
                unit,
                derived: {
                    growthPct,
                    totalCurrent, // já considera o corte quando aplicável
                },
            };
        });
    }, [performanceByStore]);

    // Ordena conforme seleção
    const sortedUnits = useMemo(() => {
        const arr = [...unitsWithMetrics];
        if (sortBy === 'GROWTH') {
            arr.sort((a, b) => b.derived.growthPct - a.derived.growthPct); // maior crescimento primeiro
        } else if (sortBy === 'REVENUE') {
            arr.sort((a, b) => b.derived.totalCurrent - a.derived.totalCurrent); // maior faturamento primeiro
        } else {
            // ALPHABETICAL
            arr.sort((a, b) => a.unit.store.localeCompare(b.unit.store, 'pt', { sensitivity: 'base' }));
        }
        return arr;
    }, [unitsWithMetrics, sortBy]);

    return (
        <>
            {/* Controles: Agrupamento + Ordenação */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

                {/* AGRUPAMENTO — só aparece quando NÃO é mobile */}
                {!isMobile && (
                    <fieldset className="w-full sm:w-auto">
                        <legend className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Agrupamento
                        </legend>

                        <div
                            role="group"
                            aria-label="Agrupar por"
                            className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden bg-white"
                        >
                            <button
                                type="button"
                                onClick={() => setGroupBy('day')}
                                aria-pressed={groupBy === 'day'}
                                className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                        ${groupBy === 'day'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'}
                        border-r border-gray-200`}
                            >
                                Por dia
                            </button>

                            <button
                                type="button"
                                onClick={() => setGroupBy('month')}
                                aria-pressed={groupBy === 'month'}
                                className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                        ${groupBy === 'month'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                Por mês
                            </button>

                            <button
                                type="button"
                                onClick={() => setGroupBy('weekDay')}
                                aria-pressed={groupBy === 'weekDay'}
                                 className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                        ${groupBy === 'weekDay'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                Por dia de semana

                            </button>
                        </div>
                    </fieldset>
                )}

                {/* ORDENAÇÃO — sempre visível */}
                <fieldset className="w-full sm:w-auto">
                    <legend className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Ordenação
                    </legend>

                    <div
                        role="group"
                        aria-label="Ordenar por"
                        className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden bg-white"
                    >
                        <button
                            type="button"
                            onClick={() => setSortBy('ALPHABETICAL')}
                            aria-pressed={sortBy === 'ALPHABETICAL'}
                            className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${sortBy === 'ALPHABETICAL'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'}
                    border-r border-gray-200`}
                        >
                            Alfabética
                        </button>

                        <button
                            type="button"
                            onClick={() => setSortBy('REVENUE')}
                            aria-pressed={sortBy === 'REVENUE'}
                            className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${sortBy === 'REVENUE'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'}
                    border-r border-gray-200`}
                        >
                            Faturamento
                        </button>

                        <button
                            type="button"
                            onClick={() => setSortBy('GROWTH')}
                            aria-pressed={sortBy === 'GROWTH'}
                            className={`px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${sortBy === 'GROWTH'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            Crescimento
                        </button>
                    </div>
                </fieldset>

            </div>

            {sortedUnits.map(({ unit, derived }) => {
                const calculateWeekDays = (obj, day) => {
                    if (!obj?.days) return 0;
                    const weekDays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']

                    return obj.days.reduce((acc, d) => {
                        const data = new Date(obj.year, obj.month - 1, Number(d.day));

                        const weekDay = weekDays[data.getDay()]

                        if (weekDay === day) {
                            return acc + Number(d.valorTotal)
                        }

                        return acc
                    }, 0)
                }
                
                const currentMonthName = months[unit.currentMonth - 1] || '';
                // console.log("currentMonthName", currentMonthName)
                const lastMonthName = months[unit.currentMonth - 2]
                const growthPct = derived.growthPct;

                //MES ATUAL
                const currentMonthObj = unit.months.find(m => m.month === unit.currentMonth)
                // console.log("currentMonthObj", currentMonthObj)
                
                //Calcula a media ignorando os dias que não houve faturamento
                                const diasComFaturamentoCurrentMonth = currentMonthObj?.days?.filter(d => d.valorTotal !== '0.00') || [];
                                const averageCurrentMonth = diasComFaturamentoCurrentMonth.length > 0
                                    ? Number(unit.totalCurrent.valorTotal) / diasComFaturamentoCurrentMonth.length
                                    : 0;

                //MES PASSADO
                const prevMonthNum = unit.currentMonth === 1 ? 12 : unit.currentMonth - 1;
                const lastMonthObj = unit.months.find(m => m.month === prevMonthNum)
                // console.log("lastMonthObj", lastMonthObj)
                const totalLastMonth = lastMonthObj?.days?.reduce?.((acc, n) => acc + Number(n.valorTotal), 0) || 0;
                const diasComFaturamentoLastMonth = lastMonthObj?.days?.filter(d => d.valorTotal !== '0.00') || [];

                //Calcula a media ignorando os dias que não houve faturamento
                const averageLastMonth = diasComFaturamentoLastMonth.length > 0 ? totalLastMonth / diasComFaturamentoLastMonth.length : 0

                //Variação da media entre um mes e outro
                const varicao = averageLastMonth > 0 ? ((averageCurrentMonth - averageLastMonth) / averageLastMonth) * 100 : 0

                // Summary (topo)
                let summary = [];

                    summary = [
                        { name: `Faturamento (${currentMonthName})`, value: currencyFormat(unit.totalCurrent.valorTotal) },
                        { name: 'Crescimento Mês vs Ant.', value: `${growthPct.toFixed(2)}%`, isGrowth: true, growthValue: growthPct },
                        { name: 'Faturamento Médio', value: currencyFormat(unit.averageOthers.valorTotal) },
                        { name: '% CMV Atual', value: unit.totalCurrent.percentualCmv + '%' },
                        { name: `Média/Dia (${currentMonthName})`, value: isNaN(averageCurrentMonth) ? '-' : currencyFormat(averageCurrentMonth) },
                        { name: `Média/Dia (${lastMonthName})`, value: isNaN(averageLastMonth) || averageLastMonth === 0 ? '-' : currencyFormat(averageLastMonth) },
                        { name: 'Variação Média/Dia', value: isNaN(varicao) ? '-' : `${varicao.toFixed(2)}%`, isGrowth: true, growthValue: varicao },
                    ];
               

                // Dados do gráfico
                let chartData, categories, indexKey;
                if (groupBy === 'day') {
                    const monthsArr = unit.months;
                    categories = monthsArr.map((m) => m.monthName);
                    indexKey = 'day';
                    const maxDays = Math.max(...monthsArr.map((m) => m.days.length));
                    chartData = Array.from({ length: maxDays }, (_, i) => {
                        const day = i + 1;
                        const entry = { day };
                        monthsArr.forEach((m) => {
                            const dayRec = m.days.find((d) => d.day === day);
                            entry[m.monthName] = dayRec ? parseFloat(dayRec.valorTotal) : 0;
                        });
                        return entry;
                    });
                } else if (groupBy === 'month') {
                    // groupBy === 'month'
                    indexKey = 'month';
                    chartData = unit.months.map((m) => ({
                        month: m.monthName,
                        'Valor Total': m.days.reduce((sum, d) => sum + parseFloat(d.valorTotal), 0),
                    }));
                    categories = ['Valor Total'];
                } else {
                    const diasCompletos = [ 
                        'Domingo',
                        'Segunda-feira',
                        'Terça-feira',
                        'Quarta-feira',
                        'Quinta-feira',
                        'Sexta-feira',
                        'Sábado',
                    ];

                    const weekDayObj = diasCompletos.map(item => {
                        return {
                            day: item,
                            currentMonth: calculateWeekDays(currentMonthObj, item),
                            lastMonth: calculateWeekDays(lastMonthObj, item)
                        }

                    })
                    // console.log("weekDayObj", weekDayObj)

                    indexKey = 'weekDay'
                    chartData = weekDayObj.map((w) => ({
                        weekDay: w.day,
                        'Mês Atual': w.currentMonth,
                        'Mês Anterior': w.lastMonth,
                    }));
                    categories = ['Mês Atual', 'Mês Anterior']
                }

                const colorsForChart =
                    groupBy === 'day'
                        ? ['blue', 'emerald', 'orange', 'slate', 'zinc', 'cyan']
                        : groupBy === 'weekDay'
                            ? [growthPct >= 0 ? 'emerald' : 'orange', 'cyan']
                            : [growthPct >= 0 ? 'emerald' : 'orange'];

                return (
                    <Card key={unit.store} className="flex flex-col space-y-4 p-4 w-full mb-6">
                        <div className="flex flex-col gap-x-6 gap-y-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{unit.store}</h3>

                            <div className="mt-4 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:items-center sm:gap-x-8 sm:grid-cols-none">
                                {summary.map((item, idx) => (
                                    <React.Fragment key={item.name}>
                                        <div className="w-full sm:w-auto min-w-0">
                                            {item.tooltip ? (
                                                <Tooltip title={item.tooltip}>
                                                    <p className="whitespace-nowrap text-sm text-gray-500 hover:underline cursor-help">
                                                        {item.name}
                                                    </p>
                                                </Tooltip>
                                            ) : (
                                                <p className="whitespace-nowrap text-sm text-gray-500">{item.name}</p>
                                            )}
                                            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-50">{item.value}</p>
                                        </div>

                                        {!isMobile && idx < summary.length - 1 && (
                                            <span className="h-10 w-px bg-slate-500/20" aria-hidden="true" />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {['month', 'day'].includes(groupBy) ? (
                            <AreaChart
                                data={chartData}
                                index={indexKey}
                                categories={categories}
                                colors={colorsForChart}
                                stack={false}
                                valueFormatter={valueFormatter}
                                showLegend
                                showYAxis
                                className="mt-10 hidden sm:block"
                                yAxisWidth={80}
                            />
                        ) : (
                                <BarChart
                                    data={chartData}
                                    index={indexKey}
                                    categories={categories}
                                    colors={colorsForChart}
                                    stack={false}
                                    valueFormatter={valueFormatter}
                                    showLegend
                                    showYAxis
                                    className="mt-10 hidden sm:block"
                                    yAxisWidth={80}
                                />
                        )}
                    </Card>
                );
            })}
        </>
    );
}
